// ---------------------------------------------------------------------------
// BackToTop Renderer (editor key: BackToTop)
// ---------------------------------------------------------------------------
// Navigation primitive — publishes the editor `BackToTop` block as a static
// in-page anchor link pointing at the `#top` fragment. The editor preview
// renders a JS-controlled button that appears after scrolling, but the
// canonical renderer ships only the visual affordance: a hyperlink that
// jumps to the top of the page. Any runtime "show after Npx scroll" behavior
// is handled by theme JS using the `data-ease-show-after-px` data attribute,
// not by inline `<script>`. Position is restricted to `br` (bottom-right) or
// `bl` (bottom-left) class hooks; unknown values fall back to `br`.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";

interface BackToTopProps {
  label?: string;
  showAfterPx?: number | string;
  position?: "br" | "bl";
}

const ALLOWED_POSITIONS = new Set(["br", "bl"]);
const MIN_SHOW_AFTER = 0;
const MAX_SHOW_AFTER = 10_000;
const DEFAULT_SHOW_AFTER = 300;

function normalizePosition(value: unknown): "br" | "bl" {
  if (typeof value === "string" && ALLOWED_POSITIONS.has(value)) {
    return value as "br" | "bl";
  }
  return "br";
}

function normalizeShowAfter(value: unknown): number {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return DEFAULT_SHOW_AFTER;
  const rounded = Math.round(num);
  if (rounded < MIN_SHOW_AFTER) return MIN_SHOW_AFTER;
  if (rounded > MAX_SHOW_AFTER) return MAX_SHOW_AFTER;
  return rounded;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as BackToTopProps;

  const position = normalizePosition(p.position);
  const showAfterPx = normalizeShowAfter(p.showAfterPx);
  const label = typeof p.label === "string" ? p.label.trim() : "";

  const labelHtml = label
    ? `<span class="ease-back-to-top__label">${text(label)}</span>`
    : "";

  return `<div class="ease-section ease-back-to-top ease-back-to-top--${attr(position)}" data-ease-show-after-px="${attr(String(showAfterPx))}">
  <a class="ease-back-to-top__link" href="#top" aria-label="Back to top">
    <span class="ease-back-to-top__icon" aria-hidden="true">&uarr;</span>${labelHtml}
  </a>
</div>`;
}

registerRenderer("back-to-top", { render });
